import React, { useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Text from '~/components/core/TextComponents/Text';
import { getSectionConditionFieldsUrl } from '~/components/GenericTemplates/utils/routes';
import {
  hasAuthoredCondition,
  RuleConditionsQueryBuilder,
  validateConditionsTree,
} from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';
import { useConditionFieldsFetcher } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/useConditionFieldsFetcher';

export interface SectionConditionScope {
  organizationId: number;
  lobs: string[];
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
}

interface ConditionalSectionDialogProps {
  initialLabel: string;
  initialQuery: RuleGroupType | null;
  scope: SectionConditionScope;
  onSave: (label: string, query: RuleGroupType) => void;
  onClose: () => void;
}

/** The condition of one section, on the template's LOB and sub-organization scope, as the save validates it. */
const ConditionalSectionDialog: React.FC<ConditionalSectionDialogProps> = ({
  initialLabel,
  initialQuery,
  scope,
  onSave,
  onClose,
}) => {
  const [label, setLabel] = useState(initialLabel);
  const [query, setQuery] = useState<RuleGroupType | null>(initialQuery);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // The templates module's own catalog route: the shared rules-engine one asks for SYSCONFIG_AUTOMATION,
  // and whoever may write the template may write its section conditions.
  const { fields, isLoading } = useConditionFieldsFetcher({
    url: getSectionConditionFieldsUrl(scope.organizationId),
    enabled: true,
    queryParams: {
      lobs: scope.lobs,
      is_all_lobs: scope.isAllLobs,
      is_all_sub_orgs: scope.isAllSubOrgs,
      sub_org_ids: scope.subOrgIds ?? [],
    },
  });

  const labelError = isSubmitted && !label.trim() ? 'Required' : undefined;
  const conditionError = !hasAuthoredCondition(query)
    ? 'At least one condition is required'
    : validateConditionsTree(query, fields);

  const handleSave = () => {
    setIsSubmitted(true);
    if (!label.trim() || conditionError || !query) {
      return;
    }
    onSave(label.trim(), query);
  };

  return (
    <CardDialog
      isDialog
      open
      title="Show this section only if…"
      maxWidth="md"
      fullWidth
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSave}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isLoading}
        />
      }
    >
      <div className="flex flex-col gap-16 pt-12">
        <TextField
          label="Section name"
          placeholder="For example: Open claims only"
          value={label}
          onChange={(newValue: string) => setLabel(newValue)}
          error={!!labelError}
          helperText={labelError}
          fullWidth
        />
        <RuleConditionsQueryBuilder
          value={query}
          onChange={setQuery}
          shouldShowErrors={isSubmitted}
          fields={fields}
          isLoading={isLoading}
          prerequisiteMessage={null}
        />
        {isSubmitted && conditionError ? (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.ERROR}>
            {conditionError}
          </Text>
        ) : null}
      </div>
    </CardDialog>
  );
};

export default ConditionalSectionDialog;
